import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import logo from "../../assets/logo.png";
import { startTelemetry } from "../../lib/telemetry-client";
import { authService } from "../../services/auth.service";
import { useAuthStore } from "../../stores/authStore";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const resetSuccess = searchParams.get("reset") === "success";

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginData) => {
    setIsLoading(true);

    try {
      const response = await authService.login(data);

      setAuth(response.user, response.accessToken, response.refreshToken);

      // Start telemetry SDK after successful login
      startTelemetry({
        id: response.user.id,
        tenantId: response.user.tenantId,
      });

      toast.success(`Welcome back, ${response.user.firstName}!`);

      setTimeout(() => navigate("/dashboard"), 500);
    } catch (err: any) {
      setIsLoading(false);

      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Invalid email or password";

      toast.error(errorMessage, {
        duration: 6000,
        position: "top-center",
        style: {
          borderLeft: "4px solid #ef4444",
          fontWeight: "600",
          fontSize: "16px",
          padding: "16px 24px",
        },
      });
    }
  };

  return (
    <div className="h-screen grid lg:grid-cols-2 bg-slate-50 relative overflow-hidden">
      {/* Decorative Blobs for depth */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/50 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2 -z-10" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-teal-50/50 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4 -z-10" />

      {/* Left Side - Immersive Hero Section with Blue/Teal Branding */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden bg-blue-600 text-white border-r rounded-tr-[50px] rounded-br-[50px]">
        {/* Elite Aurora Background - Blue/Teal Theme */}

        {/* Elite Aurora Background - Blue/Teal Theme */}
        <div className="absolute inset-0 z-0 text-white">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600" />
          <div className="absolute top-[-10%] left-[-10%] w-[100%] h-[100%] rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[80%] h-[80%] rounded-full bg-white/10 blur-[100px] animate-pulse delay-700" />
          <div className="absolute top-[20%] left-[10%] w-[60%] h-[60%] rounded-full bg-blue-400/20 blur-[110px] animate-pulse delay-1000" />
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-soft-light" />
        </div>

        {/* Branding - Elite Logo Pill */}
        <div className="relative z-10">
          <div className="flex items-center gap-3.5 bg-white backdrop-blur-2xl py-3 px-6 rounded-2xl border border-white/20 shadow-2xl w-fit group hover:bg-white/20 transition-all duration-300">
            {/* <div className="bg-white p-1.5 rounded-xl shadow-inner">
              <img
                src={logo}
                alt="MediCorePay Logo"
                className="w-10 h-10 object-contain"
              />
            </div> */}
            <div className="flex flex-col text-white">
              <span className="text-5xl font-black tracking-tight leading-none">
                {/* MediCorePay */}
                <span className="text-blue-800">Medi</span>
                <span className="text-red-700">Core</span>
                <span className="text-teal-700">Pay</span>
              </span>
              {/* <span className="text-[10px] uppercase tracking-widest text-blue-100 font-bold mt-1">Enterprise POS</span> */}
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-lg space-y-10">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white text-xs font-bold tracking-wider uppercase">
              <span className="flex h-1.5 w-1.5 rounded-full bg-teal-300 animate-ping" />
              Build v{__MEDICOREPAY_VERSION__} · {__MEDICOREPAY_REVISION__}
            </div>
            <h1 className="text-6xl font-black leading-[1.1] tracking-tight text-white">
              Elevate Your <br />
              <span className="text-teal-400">Healthcare</span>
              <br />
              Commerce
            </h1>
            <p className="text-xl text-blue-50 font-light leading-relaxed max-w-md">
              The most advanced point of sale system designed specifically for
              the modern pharmaceutical industry.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-5 rounded-3xl bg-white/10 border border-white/10 backdrop-blur-sm hover:bg-white/15 transition-colors group">
              <div className="w-10 h-10 rounded-xl bg-teal-400/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <ArrowRight className="w-5 h-5 text-teal-100" />
              </div>
              <h3 className="font-bold text-white text-sm">Lightning FAST</h3>
              <p className="text-xs text-blue-100 mt-1">
                Sub-second transaction processing
              </p>
            </div>
            <div className="p-5 rounded-3xl bg-white/10 border border-white/10 backdrop-blur-sm hover:bg-white/15 transition-colors group">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Lock className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-bold text-white text-sm">Secure Cloud</h3>
              <p className="text-xs text-blue-100 mt-1">
                End-to-end encrypted medical data
              </p>
            </div>
          </div>
        </div>

        <div className="relative z-10 flex justify-between items-end">
          <p className="text-sm text-blue-100 font-medium">
            © 2026 MediCorePay.
          </p>
          <nav aria-label="Legal" className="flex gap-8">
            <Link
              to="/privacy"
              className="text-blue-100 hover:text-white transition-colors text-sm font-semibold"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="text-blue-100 hover:text-white transition-colors text-sm font-semibold"
            >
              Terms of Use
            </Link>
          </nav>
        </div>
      </div>

      {/* Right Side - Elite Sign In Form */}
      <div className="flex flex-col items-center justify-center p-8 lg:p-24 w-full relative overflow-y-auto">
        <div className="w-full max-w-[460px] space-y-12 animate-slide-up">
          {/* Logo above Sign In - X-Large format */}
          <div className="flex flex-col items-center mb-8">
            {/* <div className="p-5 shadow-2xl shadow-blue-500/30 mb-8 group hover:scale-105 transition-all duration-500">
              <img
                src={logo}
                alt="MediCorePay Logo"
                className="w-20 h-20 object-contain"
              />
            </div> */}
            <img
              src={logo}
              alt="MediCorePay Logo"
              className="w-25 h-25 object-contain max-w-[200px] mb-8 group hover:scale-105 transition-all duration-500"
            />
            <div className="space-y-4 text-center">
              <h2 className="text-5xl font-black text-slate-900 tracking-tight">
                Sign In
              </h2>
              <p className="text-slate-500 text-lg font-medium">
                Access your pharmacy dashboard
              </p>
            </div>
          </div>

          {resetSuccess && (
            <div className="p-4 bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-2xl text-sm font-semibold flex items-center shadow-sm animate-fade-in">
              <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3 animate-pulse" />
              Success! Your password has been updated.
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(onSubmit)(e);
            }}
            className="space-y-8"
          >
            <div className="space-y-3">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">
                Email Address
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                  <Mail className="h-5 w-5" />
                </div>
                <input
                  {...register("email")}
                  className="block w-full pl-12 pr-4 py-4 bg-slate-100/50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500/20 focus:ring-4 focus:ring-blue-500/5 transition-all duration-300 outline-none text-slate-900 placeholder-slate-400 font-bold"
                  placeholder="name@company.com"
                />
              </div>
              {errors.email && (
                <p className="text-xs text-red-500 font-bold ml-1 flex items-center gap-1.5 animate-slide-up">
                  <span className="w-1 h-1 rounded-full bg-red-500" />
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between ml-1">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-black text-indigo-600 hover:text-indigo-700 transition-colors uppercase tracking-[0.15em]"
                >
                  Forgot Password?
                </Link>
              </div>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-indigo-500 transition-colors">
                  <Lock className="h-5 w-5" />
                </div>
                <input
                  {...register("password")}
                  type={showPassword ? "text" : "password"}
                  className="block w-full pl-12 pr-12 py-4 bg-slate-100/50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-500/20 focus:ring-4 focus:ring-indigo-500/5 transition-all duration-300 outline-none text-slate-900 placeholder-slate-400 font-bold"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400 transition-colors hover:text-indigo-600 focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-red-500 font-bold ml-1 flex items-center gap-1.5 animate-slide-up">
                  <span className="w-1 h-1 rounded-full bg-red-500" />
                  {errors.password.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full relative group py-5 px-6 bg-slate-950 hover:bg-black text-white font-black text-lg rounded-2xl shadow-2xl active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <span className="relative z-10 flex items-center justify-center gap-3">
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin h-6 w-6" />
                    Synchronizing...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="h-6 w-6 group-hover:translate-x-1.5 transition-transform duration-300" />
                  </>
                )}
              </span>
            </button>
          </form>

          <div className="pt-8 text-center border-t border-slate-100">
            <p className="text-slate-500 font-bold text-sm">
              New to MediCorePay?{" "}
              <Link
                to="/contact-us"
                className="text-indigo-600 hover:text-indigo-700 font-black underline underline-offset-4 decoration-2 decoration-indigo-200 hover:decoration-indigo-500 transition-all ml-1"
              >
                Contact Us
              </Link>
            </p>
            <nav
              aria-label="Legal"
              className="mt-6 flex items-center justify-center gap-6 text-xs font-semibold text-slate-500 lg:hidden"
            >
              <Link
                to="/privacy"
                className="hover:text-indigo-700 underline underline-offset-4 transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="hover:text-indigo-700 underline underline-offset-4 transition-colors"
              >
                Terms of Use
              </Link>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
